(() => {
  if (window.tengliCreatorNetworkInit) {
    window.tengliCreatorNetworkInit(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const settledProgressDelays = [0, 60, 180, 420, 900];
  const mobileResumeDelay = 5000;
  const mobilePixelsPerSecond = 6;

  class TengliCreatorNetwork {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.tengli-creator-network__viewport');
      this.track = root.querySelector('[data-tengli-creator-track]') || root.querySelector('.tengli-creator-network__track');
      this.sourceList = root.querySelector('[data-tengli-creator-list]');
      this.cloneList = root.querySelector('[data-tengli-creator-clones]');
      this.progress = root.querySelector('[data-tengli-creator-progress]');
      this.progressBar = root.querySelector('[data-tengli-creator-progress-bar]');
      this.progressTimers = [];
      this.mobilePaused = false;
      this.mobileAutoFrame = null;
      this.mobileResumeTimer = null;
      this.mobileLastTime = 0;
      this.mobileAutoLeft = null;
      this.rebuild = this.rebuild.bind(this);
      this.scheduleRebuild = this.scheduleRebuild.bind(this);
      this.updateProgress = this.updateProgress.bind(this);
      this.scheduleProgressUpdate = this.scheduleProgressUpdate.bind(this);
      this.scheduleSettledProgressUpdate = this.scheduleSettledProgressUpdate.bind(this);
      this.pauseMobileAutoplay = this.pauseMobileAutoplay.bind(this);
      this.resumeMobileAutoplay = this.resumeMobileAutoplay.bind(this);
      this.scheduleMobileResume = this.scheduleMobileResume.bind(this);
      this.stepMobileAutoplay = this.stepMobileAutoplay.bind(this);
    }

    connect() {
      if (!this.viewport || !this.track || !this.sourceList || !this.cloneList) return;

      this.resizeObserver = new ResizeObserver(this.scheduleRebuild);
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);
      this.resizeObserver.observe(this.sourceList);

      this.viewport.addEventListener('scroll', this.scheduleProgressUpdate, { passive: true });
      this.viewport.addEventListener('pointerdown', this.pauseMobileAutoplay, { passive: true });
      this.viewport.addEventListener('pointerup', this.scheduleMobileResume, { passive: true });
      this.viewport.addEventListener('pointercancel', this.scheduleMobileResume, { passive: true });
      this.viewport.addEventListener('touchstart', this.pauseMobileAutoplay, { passive: true });
      this.viewport.addEventListener('touchend', this.scheduleMobileResume, { passive: true });
      this.viewport.addEventListener('wheel', this.scheduleMobileResume, { passive: true });
      this.viewport.addEventListener('focusin', this.pauseMobileAutoplay);
      this.viewport.addEventListener('focusout', this.scheduleMobileResume);
      window.addEventListener('resize', this.scheduleSettledProgressUpdate);
      document.addEventListener('visibilitychange', this.scheduleRebuild);

      if (reduceMotionQuery.addEventListener) {
        reduceMotionQuery.addEventListener('change', this.scheduleRebuild);
        mobileQuery.addEventListener('change', this.scheduleRebuild);
      } else {
        reduceMotionQuery.addListener(this.scheduleRebuild);
        mobileQuery.addListener(this.scheduleRebuild);
      }

      this.rebuild();
      this.scheduleSettledProgressUpdate();
      if (document.fonts?.ready) document.fonts.ready.then(this.scheduleSettledProgressUpdate);
    }

    disconnect() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.progressFrame);
      this.stopMobileAutoplay();
      this.clearProgressTimers();
      this.resizeObserver?.disconnect();
      this.viewport?.removeEventListener('scroll', this.scheduleProgressUpdate);
      this.viewport?.removeEventListener('pointerdown', this.pauseMobileAutoplay);
      this.viewport?.removeEventListener('pointerup', this.scheduleMobileResume);
      this.viewport?.removeEventListener('pointercancel', this.scheduleMobileResume);
      this.viewport?.removeEventListener('touchstart', this.pauseMobileAutoplay);
      this.viewport?.removeEventListener('touchend', this.scheduleMobileResume);
      this.viewport?.removeEventListener('wheel', this.scheduleMobileResume);
      this.viewport?.removeEventListener('focusin', this.pauseMobileAutoplay);
      this.viewport?.removeEventListener('focusout', this.scheduleMobileResume);
      window.removeEventListener('resize', this.scheduleSettledProgressUpdate);
      document.removeEventListener('visibilitychange', this.scheduleRebuild);
      if (reduceMotionQuery.removeEventListener) {
        reduceMotionQuery.removeEventListener('change', this.scheduleRebuild);
        mobileQuery.removeEventListener('change', this.scheduleRebuild);
      } else {
        reduceMotionQuery.removeListener(this.scheduleRebuild);
        mobileQuery.removeListener(this.scheduleRebuild);
      }
    }

    clearProgressTimers() {
      this.progressTimers.forEach((timer) => window.clearTimeout(timer));
      this.progressTimers = [];
    }

    scheduleRebuild() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.rebuild);
      this.scheduleSettledProgressUpdate();
    }

    scheduleProgressUpdate(delay = 0) {
      if (delay > 0) {
        const timer = window.setTimeout(() => {
          this.progressTimers = this.progressTimers.filter((queuedTimer) => queuedTimer !== timer);
          this.scheduleProgressUpdate();
        }, delay);
        this.progressTimers.push(timer);
        return;
      }

      cancelAnimationFrame(this.progressFrame);
      this.progressFrame = requestAnimationFrame(this.updateProgress);
    }

    scheduleSettledProgressUpdate() {
      this.clearProgressTimers();
      settledProgressDelays.forEach((delay) => this.scheduleProgressUpdate(delay));
    }

    hideProgress() {
      if (!this.progress) return;
      this.progress.hidden = true;
      this.progress.style.removeProperty('--tengli-creator-progress-size');
      this.progress.style.removeProperty('--tengli-creator-progress-offset');
    }

    updateProgress() {
      if (!this.progress || !this.progressBar || !this.viewport) return;

      if (!mobileQuery.matches) {
        this.hideProgress();
        return;
      }

      const viewportWidth = this.viewport.clientWidth;
      const scrollWidth = this.viewport.scrollWidth;

      if (!viewportWidth || !scrollWidth) {
        this.progress.hidden = false;
        this.scheduleProgressUpdate(140);
        return;
      }

      const maxScrollLeft = scrollWidth - viewportWidth;
      if (maxScrollLeft <= 1) {
        this.hideProgress();
        return;
      }

      this.progress.hidden = false;

      const progressWidth = this.progress.clientWidth;
      if (!progressWidth) {
        this.scheduleProgressUpdate(140);
        return;
      }

      const thumbSize = Math.min(1, Math.max(viewportWidth / scrollWidth, 0.22));
      const scrollRatio = Math.min(1, Math.max(0, this.viewport.scrollLeft / maxScrollLeft));
      const thumbOffset = progressWidth * (1 - thumbSize) * scrollRatio;

      this.progress.style.setProperty('--tengli-creator-progress-size', thumbSize.toFixed(4));
      this.progress.style.setProperty('--tengli-creator-progress-offset', `${thumbOffset.toFixed(2)}px`);
    }

    shouldUseMobileAutoplay() {
      return mobileQuery.matches &&
        !reduceMotionQuery.matches &&
        !document.hidden &&
        this.root.classList.contains('tengli-creator-network--autoplay') &&
        this.viewport &&
        this.viewport.scrollWidth > this.viewport.clientWidth + 1;
    }

    pauseMobileAutoplay() {
      if (!mobileQuery.matches) return;
      this.mobilePaused = true;
      this.root.removeAttribute('data-mobile-autoplaying');
      window.clearTimeout(this.mobileResumeTimer);
      this.mobileLastTime = 0;
      this.mobileAutoLeft = null;
    }

    scheduleMobileResume() {
      if (!mobileQuery.matches) return;
      window.clearTimeout(this.mobileResumeTimer);
      this.mobileResumeTimer = window.setTimeout(this.resumeMobileAutoplay, mobileResumeDelay);
    }

    resumeMobileAutoplay() {
      this.mobilePaused = false;
      this.mobileLastTime = 0;
      this.startMobileAutoplay();
    }

    startMobileAutoplay() {
      this.stopMobileAutoplay(false);
      if (!this.shouldUseMobileAutoplay()) return;
      this.root.setAttribute('data-mobile-autoplaying', 'true');
      this.mobileAutoFrame = requestAnimationFrame(this.stepMobileAutoplay);
    }

    stopMobileAutoplay(clearResume = true) {
      cancelAnimationFrame(this.mobileAutoFrame);
      this.mobileAutoFrame = null;
      this.mobileLastTime = 0;
      this.mobileAutoLeft = null;
      this.root.removeAttribute('data-mobile-autoplaying');
      if (clearResume) window.clearTimeout(this.mobileResumeTimer);
    }

    stepMobileAutoplay(timestamp) {
      if (!this.shouldUseMobileAutoplay()) {
        this.stopMobileAutoplay(false);
        return;
      }

      if (this.mobilePaused) {
        this.mobileLastTime = 0;
        this.mobileAutoFrame = requestAnimationFrame(this.stepMobileAutoplay);
        return;
      }

      if (!this.mobileLastTime) this.mobileLastTime = timestamp;
      const elapsed = Math.min(64, timestamp - this.mobileLastTime);
      this.mobileLastTime = timestamp;

      const maxScrollLeft = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (maxScrollLeft > 1) {
        if (this.mobileAutoLeft == null) this.mobileAutoLeft = this.viewport.scrollLeft;
        this.mobileAutoLeft += (elapsed / 1000) * mobilePixelsPerSecond;
        if (this.mobileAutoLeft >= maxScrollLeft - 1) {
          this.mobileAutoLeft = 0;
          this.viewport.scrollLeft = 0;
          this.mobilePaused = true;
          this.scheduleMobileResume();
        } else {
          this.viewport.scrollLeft = this.mobileAutoLeft;
        }
        this.scheduleProgressUpdate();
      }

      this.mobileAutoFrame = requestAnimationFrame(this.stepMobileAutoplay);
    }

    rebuild() {
      this.root.removeAttribute('data-ready');
      this.cloneList.replaceChildren();
      this.track.style.removeProperty('--tengli-creator-distance');
      this.stopMobileAutoplay(false);
      this.scheduleSettledProgressUpdate();

      if (mobileQuery.matches) {
        this.root.setAttribute('data-mobile-ready', 'true');
        this.startMobileAutoplay();
        return;
      }

      this.root.removeAttribute('data-mobile-ready');
      if (!this.root.classList.contains('tengli-creator-network--autoplay') || reduceMotionQuery.matches) return;

      const cards = Array.from(this.sourceList.children);
      const sourceWidth = this.sourceList.getBoundingClientRect().width;
      const viewportWidth = this.viewport.clientWidth;
      const firstCardWidth = cards[0]?.getBoundingClientRect().width || 0;

      if (!cards.length || !sourceWidth || !viewportWidth || !firstCardWidth) return;

      const targetCloneWidth = viewportWidth + firstCardWidth * 2;
      let cloneWidth = 0;
      let index = 0;
      const maxClones = Math.max(4, Math.ceil(targetCloneWidth / firstCardWidth) + 2);

      while (cloneWidth < targetCloneWidth && index < maxClones) {
        const clone = cards[index % cards.length].cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        this.cloneList.append(clone);
        cloneWidth = this.cloneList.getBoundingClientRect().width;
        index += 1;
      }

      this.track.style.setProperty('--tengli-creator-distance', `${sourceWidth}px`);
      requestAnimationFrame(() => {
        this.root.setAttribute('data-ready', 'true');
        this.scheduleSettledProgressUpdate();
      });
    }
  }

  const getRoots = (container = document) => {
    const roots = [];
    if (container.matches?.('.tengli-creator-network')) roots.push(container);
    container.querySelectorAll?.('.tengli-creator-network').forEach((root) => roots.push(root));
    return roots;
  };

  const init = (container = document) => {
    getRoots(container).forEach((root) => {
      let instance = instances.get(root);
      if (!instance) {
        instance = new TengliCreatorNetwork(root);
        instances.set(root, instance);
        instance.connect();
      }
      instance.scheduleSettledProgressUpdate();
      instance.scheduleRebuild();
    });
  };

  const refreshFromEvent = (event) => {
    init(event.target);
    getRoots(event.target).forEach((root) => instances.get(root)?.scheduleSettledProgressUpdate());
  };

  window.tengliCreatorNetworkInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', () => init());
  document.addEventListener('shopify:section:load', refreshFromEvent);
  document.addEventListener('shopify:section:select', refreshFromEvent);
  document.addEventListener('shopify:block:select', refreshFromEvent);
  document.addEventListener('shopify:block:deselect', refreshFromEvent);
  document.addEventListener('shopify:section:unload', (event) => {
    getRoots(event.target).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.disconnect();
      instances.delete(root);
    });
  });
})();